import { toCapability, toRole, toSection, toUser } from './securityMappers'
import type { AdminCapability, AdminRole, AdminSection, AdminUser } from '../../types'
import {
  requestAuthed,
  requestAuthedJson,
  resolveTokenFromAuthHeaders,
  toErrorMessage,
  type LegacyAuthHeaders,
} from '../../lib/api/http'

type Translator = (key: string, params?: Record<string, string | number>) => string

type TeamSnapshot = {
  users: AdminUser[]
  roles: AdminRole[]
  capabilities: AdminCapability[]
  sections: AdminSection[]
}

const parseUsersPayload = (t: Translator, payload: unknown): Pick<TeamSnapshot, 'users' | 'roles'> => {
  const source = payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {}
  const usersRaw = Array.isArray(source.users) ? source.users : []
  const rolesRaw = Array.isArray(source.roles) ? source.roles : []

  return {
    users: usersRaw.map((user) => toUser(t, user)).filter((user) => Boolean(user.id)),
    roles: rolesRaw.map(toRole).filter((role) => Boolean(role.id)),
  }
}

const parseCapabilitiesPayload = (payload: unknown): AdminCapability[] =>
  Array.isArray(payload) ? payload.map(toCapability).filter((capability) => capability.id) : []

const parseSectionsPayload = (payload: unknown): AdminSection[] =>
  Array.isArray(payload) ? payload.map(toSection).filter((section) => section.id) : []

export const fetchTeamSnapshot = async ({
  authHeaders,
  t,
}: {
  authHeaders: LegacyAuthHeaders
  t: Translator
}): Promise<TeamSnapshot> => {
  const token = resolveTokenFromAuthHeaders(authHeaders)
  const [usersRes, capabilitiesRes, sectionsRes] = await Promise.all([
    requestAuthed({ token, path: '/admin/users' }),
    requestAuthed({ token, path: '/admin/capabilities' }),
    requestAuthed({ token, path: '/admin/sections' }),
  ])

  if (!usersRes.response.ok) throw new Error(t('security.team.loadError'))

  const usersData = usersRes.payload
  const { users, roles } = parseUsersPayload(t, usersData)

  const capabilitiesData = capabilitiesRes.response.ok ? capabilitiesRes.payload : null
  const sectionsData = sectionsRes.response.ok ? sectionsRes.payload : null

  return {
    users,
    roles,
    capabilities: parseCapabilitiesPayload(capabilitiesData),
    sections: parseSectionsPayload(sectionsData),
  }
}

export const requestCreateUser = async ({
  authHeaders,
  payload,
  t,
}: {
  authHeaders: LegacyAuthHeaders
  payload: {
    username: string
    password: string
    displayName?: string
    email?: string
    roles: string[]
  }
  t: Translator
}): Promise<AdminUser> => {
  const token = resolveTokenFromAuthHeaders(authHeaders)
  const { response, payload: body } = await requestAuthed({
    token,
    path: '/admin/users',
    init: {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    },
  })

  if (!response.ok) {
    throw new Error(toErrorMessage(body, t('security.team.createError')))
  }

  const data = body as { user?: unknown }
  return toUser(t, data?.user)
}

export const requestUpdateUserRoles = async ({
  authHeaders,
  id,
  payload,
  t,
}: {
  authHeaders: LegacyAuthHeaders
  id: string
  payload: { roles: string[]; displayName?: string; email?: string; password?: string }
  t: Translator
}): Promise<AdminUser> => {
  const token = resolveTokenFromAuthHeaders(authHeaders)
  const { response, payload: body } = await requestAuthed({
    token,
    path: `/admin/users/${encodeURIComponent(id)}`,
    init: {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    },
  })

  if (!response.ok) {
    throw new Error(toErrorMessage(body, t('security.team.updateUserError')))
  }

  const data = body as { user?: unknown }
  return toUser(t, data?.user)
}

export const requestCreateRole = async ({
  authHeaders,
  payload,
  t,
}: {
  authHeaders: LegacyAuthHeaders
  payload: { label: string; name?: string; description?: string }
  t: Translator
}): Promise<AdminRole> => {
  const token = resolveTokenFromAuthHeaders(authHeaders)
  const data = await requestAuthedJson({
    token,
    path: '/admin/roles',
    init: {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    },
    fallbackError: t('security.team.createRoleError'),
  })

  return toRole(data?.role)
}
